import { useCallback, useContext, useEffect, useState } from "react";
import logger from "../services/Logger";
import { TourContext } from "../services/TourContext";
import "./Tour.css";
import { LocationContext } from "../services/LocationContext";
// import Table from "../components/Table";
// import ScheduleAction from "../components/ScheduleActions";
import { getAddressFromString } from "../services/directions";
import moment from "moment";
import DragAndDropTable from "../components/DragAndDropTable";

const Tour = () => {
  const { tourInfo, setTourInfo } = useContext(TourContext);
  const { locationData, getLocationAddresses } = useContext(LocationContext);
  const [locationsToSchedule, setLocationsToSchedule] = useState([]);

  // Ensure we have a default value for timeOnProperty if none exists
  const defaultTimeOnProperty = 15; // Default to 15 minutes if no value is specified

  // Local state for editable fields - only keeping startTime in the card
  const [startTime, setStartTime] = useState(tourInfo.startTime || "");

  // Keep timeOnProperty in state as the default value but don't show it in UI
  const [timeOnProperty, setTimeOnProperty] = useState(tourInfo.timeOnProperty ? parseInt(tourInfo.timeOnProperty) : defaultTimeOnProperty);

  // Update local state when tourInfo changes
  useEffect(() => {
    setStartTime(tourInfo.startTime || "");
    // Ensure timeOnProperty is always a number
    const numericValue = tourInfo.timeOnProperty ? parseInt(tourInfo.timeOnProperty) : defaultTimeOnProperty;
    setTimeOnProperty(numericValue);
  }, [tourInfo]);

  // Handle changes to start time
  const handleStartTimeChange = (e) => {
    const newStartTime = e.target.value;
    setStartTime(newStartTime);
    // Update the context with the new value
    setTourInfo({
      ...tourInfo,
      startTime: newStartTime,
    });
  };

  // Handle changes to individual location's time on property
  const handleLocationTimeOnPropertyChange = (locationIndex, newValue) => {
    // Create a deep copy of the locations array
    const updatedLocations = [...locationsToSchedule];

    // Update the specific location's timeOnProperty
    updatedLocations[locationIndex] = {
      ...updatedLocations[locationIndex],
      timeOnProperty: newValue ? parseInt(newValue, 10) : defaultTimeOnProperty,
    };

    // Update the state with the new array
    setLocationsToSchedule(updatedLocations);
  };

  const findLocationAndAddToSchedule = useCallback(
    async (tourInfoItem, index, totalItems) => {
      const addressToScheduleInfo = await getAddressFromString(tourInfoItem.endLocationAddress);
      let location = null;

      for (let index = 0; index < locationData.length; index++) {
        const address = getLocationAddresses(locationData[index]);
        const addressInfo = await getAddressFromString(address);
        if (addressInfo.placeId === addressToScheduleInfo.placeId) {
          logger.info(`These are the 2 locations that matched - ${address} with ${tourInfoItem.endLocationAddress}`);
          location = locationData[index];
        }
      }

      if (location) {
        const recommendedShowingTime = moment(tourInfoItem.arrivalTime, "HH:mm");
        logger.info("This is the recommended showing time - ", tourInfoItem.arrivalTime, recommendedShowingTime);
        const minutes = recommendedShowingTime.minutes();
        const roundedMinutes = Math.ceil(minutes / 15) * 15;
        recommendedShowingTime.minutes(roundedMinutes);

        // CRITICAL FIX: Guarantee a numeric timeOnProperty value
        // First try item-specific value, then global value, finally use default
        let timeOnPropertyValue;

        if (typeof tourInfoItem.timeOnProperty === "number") {
          timeOnPropertyValue = tourInfoItem.timeOnProperty;
        } else if (typeof tourInfo.timeOnProperty === "number") {
          timeOnPropertyValue = tourInfo.timeOnProperty;
        } else {
          // Ensure we have a value by using local state or default
          timeOnPropertyValue = timeOnProperty || defaultTimeOnProperty;
        }

        // For the last item, there's no next site, so commuteTimeToNextSite should be null or undefined
        const commuteTimeValue = index < totalItems - 1 ? tourInfoItem.commuteTimeToNextSite : null;

        const locationObject = {
          startLocationAddress: tourInfoItem.startLocationAddress,
          endLocationAddress: tourInfoItem.endLocationAddress,
          arrivalTime: tourInfoItem.arrivalTime,
          recommendedShowingTime: recommendedShowingTime.format("HH:mm"),
          contactInfo: location["Showing Contact Phone"],
          requirements: location["Showing Requirements"],
          // Store as explicit number
          timeOnProperty: Number(timeOnPropertyValue),
          commuteTimeToNextSite: commuteTimeValue,
          isLastLocation: index === totalItems - 1,
        };

        return locationObject;
      }
      return null;
    },
    [locationData, getLocationAddresses, tourInfo.timeOnProperty, timeOnProperty]
  );

  useEffect(() => {
    const runLogic = async () => {
      logger.debug("Running logic with global timeOnProperty:", timeOnProperty);
      const locationsToPresent = [];
      const numberOfSitesToScheudle = tourInfo.consolidatedList.length;

      if (numberOfSitesToScheudle > 0) {
        for (let index = 0; index < numberOfSitesToScheudle; index++) {
          const locationObject = await findLocationAndAddToSchedule(tourInfo.consolidatedList[index], index, numberOfSitesToScheudle);

          if (locationObject) {
            locationsToPresent.push(locationObject);
          }
        }
      }
      setLocationsToSchedule(locationsToPresent);
    };
    runLogic();
  }, [setLocationsToSchedule, findLocationAndAddToSchedule, tourInfo, locationData, timeOnProperty]);

  useEffect(() => {
    // Check final processed data
    locationsToSchedule.forEach((loc, idx) => {
      console.log(`Location ${idx}: ${loc.endLocationAddress}`);
      console.log(`  timeOnProperty: ${loc.timeOnProperty} (type: ${typeof loc.timeOnProperty})`);
    });
  }, [locationsToSchedule]);

  const columns = [
    {
      Header: "Arrival time",
      accessor: "arrivalTime",
    },
    {
      Header: "Recommended showing time",
      accessor: "recommendedShowingTime",
    },
    {
      Header: "Starting from",
      accessor: "startLocationAddress",
    },
    {
      Header: "Property to visit",
      accessor: "endLocationAddress",
    },
    {
      Header: "Time on property",
      accessor: "timeOnProperty",
      Cell: ({ value, row }) => {
        const index = row.index;
        return (
          <div className="editable-time-cell">
            <input
              type="number"
              min="1"
              max="120"
              value={value !== undefined && value !== null ? value : defaultTimeOnProperty}
              onChange={(e) => handleLocationTimeOnPropertyChange(index, e.target.value)}
              className="time-input-cell"
            />
            <span className="time-unit">min</span>
          </div>
        );
      },
    },
    {
      Header: "Commute to next site",
      accessor: "commuteTimeToNextSite",
      Cell: ({ row }) => {
        const { commuteTimeToNextSite, isLastLocation } = row.original;

        // If it's the last location, show "Final destination"
        if (isLastLocation) {
          return "Final destination";
        }

        // Handle the case where value is 0, undefined, or null
        if (commuteTimeToNextSite === 0) return "0 min";
        return commuteTimeToNextSite ? `${commuteTimeToNextSite} min` : "Not available";
      },
    },
    {
      Header: "Contact info",
      accessor: "contactInfo",
    },
    {
      Header: "Requirements",
      accessor: "requirements",
    },
  ];
  return (
    <>
      <h1>Schedule the tour</h1>

      {/* Tour Info Section */}
      <div className="tour-info-section">
        <div className="tour-info-card">
          <h2>Tour Information</h2>
          <div className="tour-info-details">
            <div className="tour-info-item">
              <strong>Tour Start Time:</strong>
              <div className="editable-field">
                <input type="time" className="time-input" value={startTime} onChange={handleStartTimeChange} placeholder="HH:MM" />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* <div>
        <Table columns={columns} data={locationsToSchedule} showNumbers={true} />
      </div>
      <div>
        <ScheduleAction sites={locationsToSchedule} />
      </div> */}
      <div className="tour-table-container">
        <DragAndDropTable columns={columns} dataSet={locationsToSchedule} />
      </div>
    </>
  );
};

export default Tour;
